import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "./index";

// ITAM-2583/ITAM-2622: this slice carries cross-component SIGNALS only: the
// "Join" signal that must travel from <VideoConsultAlert> (Join click) to the
// app-level <ConsultOrderManager>, plus derived non-PII booleans published for
// consumers OUTSIDE the provider subtree.
//
// Ownership is unchanged: all lifecycle state (phase, dedupe,
// orderedConsultIds, grace timer, generations) belongs to
// useConsultOrderLifecycle, and all PII/draft belongs to ConsultOrderContext.
// The prohibition that still holds without exception: no draft contents, no
// prefill, no patient fields, ever.
//
// The boundary was widened deliberately in Rev 13 of the ITAM-2622 plan (3.6a):
// the logout control is a sibling of <Layout>, outside the provider, so it can
// see neither the draft nor the hook's phase, and decisions 27/28 cannot be
// implemented without a bridge.

export interface ArmedConsult {
  consultId: number;
}

export interface ConsultOrderSliceState {
  armedConsult: ArmedConsult | null;
  /**
   * Non-PII session signal: would a logout right now discard an unfinished
   * consult order or RFZO draft? A boolean, never draft contents, never
   * patient data. Derived by `isConsultDraftUnfinished` and published by
   * `useConsultOrderLifecycle`, which is the only writer.
   *
   * Named limitation: Redux here is neither persisted nor cross-tab, so a
   * second tab always reports `false`. Every existing draft in this app behaves
   * that way; closing it would need a storage-backed signal (out of scope).
   */
  hasUnfinishedConsultDraft: boolean;
}

const initialState: ConsultOrderSliceState = {
  armedConsult: null,
  hasUnfinishedConsultDraft: false,
};

const consultOrderSlice = createSlice({
  name: "consultOrder",
  initialState,
  reducers: {
    armConsultOrder: (state, action: PayloadAction<ArmedConsult>) => {
      state.armedConsult = action.payload;
    },
    clearArmed: (state) => {
      state.armedConsult = null;
    },
    resetConsultOrder: () => initialState,
    /** The ONLY writer of the unfinished-draft signal. */
    publishUnfinishedDraft: (state, action: PayloadAction<boolean>) => {
      state.hasUnfinishedConsultDraft = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Clear the signals on logout (auth slice action type = "auth/logout")...
    builder.addCase("auth/logout", () => initialState);
    // ...and on a persona-changing token refresh, which never dispatches
    // "auth/logout" (ITAM-2622, 3.6).
    builder.addCase("auth/personaExit", () => initialState);
  },
});

export const consultOrderActions = consultOrderSlice.actions;

/**
 * ITAM-2622 (3.7): the logout control's gate. `true` means a user-initiated
 * logout would destroy work, so it must confirm first. Grace detection plays
 * no part in it (decision 28, final wording).
 */
export const selectHasUnfinishedConsultDraft = (state: RootState) =>
  state.consultOrder.hasUnfinishedConsultDraft;

export default consultOrderSlice;
